import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box, Paper, Typography, TextField, Button, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, Chip, CircularProgress, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  Print as PrintIcon,
  Search as SearchIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  Replay as ReplayIcon,
  LocalShipping as DeliveryIcon,
  ShoppingCart as PurchaseIcon,
  Storefront as BranchIcon,
  AssignmentReturn as ReturnIcon,
  FileDownload as ExcelIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import ExcelJS from 'exceljs';
import { useLanguage } from '../../contexts/LanguageContext';
import custodyStatementService from '../../services/custodyStatement.service';
import { custodiesService } from '../../services/custodies.service';
import apiClient from '../../services/api';
import { CustodyStatementResult, CustodyStatementLine } from '../../types/custodyStatement.types';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

// ============================================================
// ترجمات
// ============================================================
const translations = {
  en: {
    title: 'Custody Account Statement',
    subtitle: 'View all transactions for a custody',
    selectCustody: 'Select Custody',
    fromDate: 'From Date',
    toDate: 'To Date',
    search: 'Search',
    print: 'Print',
    exportExcel: 'Excel',
    reset: 'Reset',
    noData: 'No transactions found',
    selectCustodyFirst: 'Please select a custody to view its statement',
    // Header
    custodyNumber: 'Custody #',
    employee: 'Employee',
    branch: 'Branch',
    account: 'Account',
    status: 'Status',
    initialAmount: 'Initial Amount',
    currentBalance: 'Current Balance',
    // Table
    rowNum: '#',
    date: 'Date',
    type: 'Type',
    description: 'Description',
    received: 'Received',
    spent: 'Spent',
    balance: 'Balance',
    reference: 'Reference',
    // Types
    typeInitial: 'Initial Amount',
    typePurchaseInvoice: 'Purchase Invoice',
    typeWarehouseDelivery: 'Warehouse Delivery',
    typeBranchDelivery: 'Branch Delivery',
    typeSettlementExpense: 'Settlement Expense',
    typeReturn: 'Returned',
    typeReplenishment: 'Replenishment',
    // Summary
    summary: 'Summary',
    totalReceived: 'Total Received',
    totalSpent: 'Total Spent',
    totalReturned: 'Total Returned',
    closingBalance: 'Closing Balance',
    kwd: 'KWD',
    // Status
    Open: 'Open',
    Settled: 'Settled',
    Closed: 'Closed',
    // Export
    exportSuccess: 'Excel exported successfully',
  },
  ar: {
    title: 'كشف حساب العهدة',
    subtitle: 'عرض جميع حركات العهدة',
    selectCustody: 'اختر العهدة',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    search: 'بحث',
    print: 'طباعة',
    exportExcel: 'إكسل',
    reset: 'إعادة تعيين',
    noData: 'لا توجد حركات',
    selectCustodyFirst: 'يرجى اختيار عهدة لعرض كشف حسابها',
    // Header
    custodyNumber: 'رقم العهدة',
    employee: 'الموظف',
    branch: 'الفرع',
    account: 'الحساب',
    status: 'الحالة',
    initialAmount: 'المبلغ الأولي',
    currentBalance: 'الرصيد الحالي',
    // Table
    rowNum: 'م',
    date: 'التاريخ',
    type: 'النوع',
    description: 'البيان',
    received: 'مستلم',
    spent: 'منصرف',
    balance: 'الرصيد',
    reference: 'المرجع',
    // Types
    typeInitial: 'المبلغ الأولي',
    typePurchaseInvoice: 'فاتورة شراء',
    typeWarehouseDelivery: 'تسليم مخزن',
    typeBranchDelivery: 'تسليم فرع',
    typeSettlementExpense: 'مصروفات تسوية',
    typeReturn: 'مسترد',
    typeReplenishment: 'تعزيز العهدة',
    // Summary
    summary: 'الملخص',
    totalReceived: 'إجمالي المستلم',
    totalSpent: 'إجمالي المنصرف',
    totalReturned: 'إجمالي المسترد',
    closingBalance: 'الرصيد الختامي',
    kwd: 'د.ك',
    // Status
    Open: 'مفتوحة',
    Settled: 'مُسوّاة',
    Closed: 'مغلقة',
    // Export
    exportSuccess: 'تم التصدير لإكسل بنجاح',
  },
};

// ============================================================
// ألوان أنواع الحركات
// ============================================================
const typeConfig: Record<string, { color: string; icon: React.ReactElement }> = {
  Initial: { color: '#2196f3', icon: <WalletIcon fontSize="small" /> },
  PurchaseInvoice: { color: '#ff9800', icon: <PurchaseIcon fontSize="small" /> },
  WarehouseDelivery: { color: '#9c27b0', icon: <DeliveryIcon fontSize="small" /> },
  BranchDelivery: { color: '#00bcd4', icon: <BranchIcon fontSize="small" /> },
  SettlementExpense: { color: '#f44336', icon: <ReceiptIcon fontSize="small" /> },
  Return: { color: '#4caf50', icon: <ReturnIcon fontSize="small" /> },
  Replenishment: { color: '#1976d2', icon: <WalletIcon fontSize="small" /> },
};

// ============================================================
// Component
// ============================================================
const CustodyStatementPage: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = translations[isRTL ? 'ar' : 'en'];
  const printRef = useRef<HTMLDivElement>(null);
  const { minDate, maxDate } = useFiscalDateRange();

  // State
  const [custodies, setCustodies] = useState<any[]>([]);
  const [selectedCustody, setSelectedCustody] = useState<any | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CustodyStatementResult | null>(null);
  const [companyInfo, setCompanyInfo] = useState<any>(null);

  // Load custodies + company info on mount
  useEffect(() => {
    loadCustodies();
    loadCompanyInfo();
  }, []);

  const loadCustodies = async () => {
    try {
      const response = await apiClient.get('/Custodies/lookup');
      const data = response.data;
      setCustodies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load custodies:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await apiClient.get('/CompanySettings');
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const handleSearch = async () => {
    if (!selectedCustody) {
      toast.warning(t.selectCustodyFirst, { position: 'top-center' });
      return;
    }
    setLoading(true);
    try {
      const data = await custodyStatementService.getStatement(
        selectedCustody.custodyId,
        fromDate || undefined,
        toDate || undefined
      );
      if (data && data.summary && data.custody) {
        setResult(data);
      } else {
        toast.error(isRTL ? 'لا توجد بيانات' : 'No data returned', { position: 'top-center' });
      }
    } catch (error: any) {
      console.error('Failed to load statement:', error);
      if (error.response?.status === 403) {
        toast.error(isRTL ? 'ليس لديك صلاحية' : 'Access denied', { position: 'top-center' });
      } else {
        toast.error(isRTL ? 'خطأ في تحميل الكشف' : 'Failed to load statement', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedCustody(null);
    setFromDate('');
    setToDate('');
    setResult(null);
  };

  // === Type label helper ===
  const getTypeLabel = (type: string) => {
    const key = ('type' + type) as keyof typeof t;
    return (t as any)[key] || type;
  };

  // ============================================================
  // طباعة مع لوجو الشركة
  // ============================================================
  const handlePrint = () => {
    if (!result) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const c = result.custody;
    const s = result.summary;
    const logoBase64 = companyInfo?.logoBase64 || '';
    const companyName = isRTL ? (companyInfo?.companyNameAr || companyInfo?.companyName || '') : (companyInfo?.companyName || '');
    const companyAddress = isRTL ? (companyInfo?.addressAr || companyInfo?.address || '') : (companyInfo?.address || '');
    const companyPhone = companyInfo?.phone || '';

    printWindow.document.write(`
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${t.title} - ${c.custodyNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; font-size: 12px; padding: 20px; direction: ${isRTL ? 'rtl' : 'ltr'}; }
          .company-header { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #1976d2; }
          .company-logo { max-height: 80px; max-width: 200px; margin-bottom: 8px; }
          .company-name { font-size: 20px; font-weight: bold; color: #1976d2; margin-bottom: 4px; }
          .company-details { font-size: 11px; color: #666; }
          .report-title { text-align: center; font-size: 16px; font-weight: bold; margin: 15px 0 10px; color: #333; background: #e3f2fd; padding: 8px; border-radius: 4px; }
          .report-period { text-align: center; font-size: 11px; color: #666; margin-bottom: 15px; }
          .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 15px; font-size: 11px; }
          .info-item { padding: 5px 8px; background: #f5f5f5; border-radius: 4px; }
          .info-label { font-weight: bold; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; }
          th { background: #1976d2; color: white; padding: 6px 4px; text-align: center; font-weight: bold; }
          td { padding: 5px 4px; border-bottom: 1px solid #ddd; text-align: center; }
          tr:nth-child(even) { background: #fafafa; }
          .received { color: #2e7d32; font-weight: bold; }
          .spent { color: #c62828; font-weight: bold; }
          .totals-row { background: #e3f2fd !important; font-weight: bold; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 15px; }
          .summary-card { padding: 10px; border-radius: 6px; text-align: center; border: 2px solid #ddd; }
          .summary-card .label { font-size: 10px; color: #666; margin-bottom: 4px; }
          .summary-card .value { font-size: 16px; font-weight: bold; }
          .footer { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 10px; color: #999; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="company-header">
          ${logoBase64 ? `<img src="${logoBase64}" class="company-logo" /><br/>` : ''}
          <div class="company-name">${companyName}</div>
          <div class="company-details">
            ${companyAddress ? companyAddress + '<br/>' : ''}
            ${companyPhone ? (isRTL ? 'هاتف: ' : 'Tel: ') + companyPhone : ''}
          </div>
        </div>
        <div class="report-title">${t.title}</div>
        <div class="report-period">
          ${t.custodyNumber}: ${c.custodyNumber} | ${t.employee}: ${isRTL ? c.employeeNameAr : c.employeeName}
          ${fromDate || toDate ? '<br/>' + (fromDate ? t.fromDate + ': ' + fromDate : '') + (toDate ? ' - ' + t.toDate + ': ' + toDate : '') : ''}
        </div>
        <div class="info-grid">
          <div class="info-item"><span class="info-label">${t.branch}:</span> ${isRTL ? (c.branchNameAr || '-') : (c.branchName || '-')}</div>
          <div class="info-item"><span class="info-label">${t.account}:</span> ${c.accountCode || '-'} ${c.accountCode ? '- ' + (isRTL ? c.accountNameAr : c.accountName) : ''}</div>
          <div class="info-item"><span class="info-label">${t.initialAmount}:</span> ${c.initialAmount.toFixed(3)}</div>
          <div class="info-item"><span class="info-label">${t.status}:</span> ${(t as any)[c.status] || c.status}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>${t.rowNum}</th>
              <th>${t.date}</th>
              <th>${t.type}</th>
              <th>${t.description}</th>
              <th>${t.received}</th>
              <th>${t.spent}</th>
              <th>${t.balance}</th>
              <th>${t.reference}</th>
            </tr>
          </thead>
          <tbody>
            ${result.transactions.map(tx => `
              <tr>
                <td>${tx.rowNumber}</td>
                <td>${tx.transactionDate.split('T')[0]}</td>
                <td>${(t as any)['type' + tx.transactionType] || tx.transactionType}</td>
                <td style="text-align:${isRTL ? 'right' : 'left'}">${isRTL ? tx.descriptionAr : tx.description}</td>
                <td class="received">${tx.received > 0 ? tx.received.toFixed(3) : '-'}</td>
                <td class="spent">${tx.spent > 0 ? tx.spent.toFixed(3) : '-'}</td>
                <td style="font-weight:bold;color:${tx.balance < 0 ? '#c62828' : '#333'}">${tx.balance.toFixed(3)}</td>
                <td>${tx.referenceNumber || '-'}</td>
              </tr>
            `).join('')}
            <tr class="totals-row">
              <td colspan="4" style="text-align:${isRTL ? 'left' : 'right'};font-weight:bold;">${t.summary}</td>
              <td class="received">${s.totalReceived.toFixed(3)}</td>
              <td class="spent">${(s.totalSpent + s.totalReturned).toFixed(3)}</td>
              <td style="font-weight:bold;color:${s.closingBalance < 0 ? '#c62828' : '#1565c0'}">${s.closingBalance.toFixed(3)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div class="summary">
          <div class="summary-card" style="border-color:#2196f3"><div class="label">${t.totalReceived}</div><div class="value" style="color:#2196f3">${s.totalReceived.toFixed(3)}</div></div>
          <div class="summary-card" style="border-color:#f44336"><div class="label">${t.totalSpent}</div><div class="value" style="color:#f44336">${s.totalSpent.toFixed(3)}</div></div>
          <div class="summary-card" style="border-color:#4caf50"><div class="label">${t.totalReturned}</div><div class="value" style="color:#4caf50">${s.totalReturned.toFixed(3)}</div></div>
          <div class="summary-card" style="border-color:#1976d2"><div class="label">${t.closingBalance}</div><div class="value" style="color:${s.closingBalance < 0 ? '#c62828' : '#1976d2'}">${s.closingBalance.toFixed(3)}</div></div>
        </div>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:30px;margin-top:40px;padding-top:15px;">
          <div style="text-align:center;"><div style="border-top:1px solid #333;width:70%;margin:0 auto;padding-top:8px;font-weight:bold;font-size:12px;">${isRTL ? 'حامل العهدة' : 'Custody Holder'}</div></div>
          <div style="text-align:center;"><div style="border-top:1px solid #333;width:70%;margin:0 auto;padding-top:8px;font-weight:bold;font-size:12px;">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
          <div style="text-align:center;"><div style="border-top:1px solid #333;width:70%;margin:0 auto;padding-top:8px;font-weight:bold;font-size:12px;">${isRTL ? 'المدير' : 'Manager'}</div></div>
        </div>
        <div class="footer">${companyName} - ${new Date().toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  // ============================================================
  // تصدير Excel مع لوجو الشركة — ExcelJS
  // ============================================================
  const handleExportExcel = async () => {
    if (!result) return;
    const c = result.custody;
    const s = result.summary;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(t.title);

    // === Logo ===
    if (companyInfo?.logoBase64) {
      try {
        const base64Data = companyInfo.logoBase64.split(',')[1] || companyInfo.logoBase64;
        const ext = companyInfo.logoBase64.includes('png') ? 'png' : 'jpeg';
        const imgId = wb.addImage({ base64: base64Data, extension: ext });
        ws.addImage(imgId, { tl: { col: 3, row: 0 }, ext: { width: 80, height: 80 } });
      } catch (e) { console.error('Logo error:', e); }
    }

    // === Company Name ===
    ws.mergeCells('A1:H1');
    const companyCell = ws.getCell('A1');
    companyCell.value = isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || '');
    companyCell.font = { size: 18, bold: true, color: { argb: 'FF1976D2' } };
    companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(1).height = 35;

    // === Report Title ===
    ws.mergeCells('A2:H2');
    const titleCell = ws.getCell('A2');
    titleCell.value = t.title;
    titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
    ws.getRow(2).height = 28;

    // === Custody Info ===
    ws.mergeCells('A3:H3');
    const infoCell = ws.getCell('A3');
    infoCell.value = `${t.custodyNumber}: ${c.custodyNumber}  |  ${t.employee}: ${isRTL ? c.employeeNameAr : c.employeeName}  |  ${t.branch}: ${isRTL ? (c.branchNameAr || '-') : (c.branchName || '-')}  |  ${t.initialAmount}: ${c.initialAmount.toFixed(3)}`;
    infoCell.font = { size: 10, color: { argb: 'FF555555' } };
    infoCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(3).height = 22;

    // === Empty Row ===
    ws.getRow(4).height = 8;

    // === Headers ===
    const headers = [t.rowNum, t.date, t.type, t.description, t.received, t.spent, t.balance, t.reference];
    const headerRow = ws.getRow(5);
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
    });
    headerRow.height = 24;

    // === Data Rows ===
    let rowIdx = 6;
    result.transactions.forEach((tx) => {
      const row = ws.getRow(rowIdx);
      row.getCell(1).value = tx.rowNumber;
      row.getCell(2).value = tx.transactionDate.split('T')[0];
      row.getCell(3).value = getTypeLabel(tx.transactionType);
      row.getCell(4).value = isRTL ? tx.descriptionAr : tx.description;
      row.getCell(5).value = tx.received > 0 ? tx.received : null;
      row.getCell(6).value = tx.spent > 0 ? tx.spent : null;
      row.getCell(7).value = tx.balance;
      row.getCell(8).value = tx.referenceNumber || '-';

      // تنسيق
      row.getCell(5).font = { color: { argb: 'FF2E7D32' }, bold: tx.received > 0 };
      row.getCell(6).font = { color: { argb: 'FFC62828' }, bold: tx.spent > 0 };
      row.getCell(7).font = { bold: true, color: { argb: tx.balance < 0 ? 'FFC62828' : 'FF1565C0' } };

      // ألوان الصفوف
      const bgColor = rowIdx % 2 === 0 ? 'FFF5F5F5' : 'FFFFFFFF';
      row.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } } };
      });
      row.getCell(4).alignment = { horizontal: isRTL ? 'right' : 'left', vertical: 'middle' };

      // تنسيق أرقام
      [5, 6, 7].forEach(col => { row.getCell(col).numFmt = '#,##0.000'; });

      rowIdx++;
    });

    // === Totals Row ===
    const totalsRow = ws.getRow(rowIdx);
    totalsRow.getCell(1).value = '';
    totalsRow.getCell(2).value = '';
    totalsRow.getCell(3).value = '';
    totalsRow.getCell(4).value = t.summary;
    totalsRow.getCell(4).font = { bold: true, size: 11 };
    totalsRow.getCell(4).alignment = { horizontal: isRTL ? 'left' : 'right', vertical: 'middle' };
    totalsRow.getCell(5).value = s.totalReceived;
    totalsRow.getCell(5).font = { bold: true, color: { argb: 'FF2E7D32' }, size: 11 };
    totalsRow.getCell(6).value = s.totalSpent + s.totalReturned;
    totalsRow.getCell(6).font = { bold: true, color: { argb: 'FFC62828' }, size: 11 };
    totalsRow.getCell(7).value = s.closingBalance;
    totalsRow.getCell(7).font = { bold: true, color: { argb: s.closingBalance < 0 ? 'FFC62828' : 'FF1565C0' }, size: 12 };
    totalsRow.getCell(8).value = '';
    totalsRow.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'double', color: { argb: 'FF1976D2' } }, bottom: { style: 'double', color: { argb: 'FF1976D2' } } };
    });
    [5, 6, 7].forEach(col => { totalsRow.getCell(col).numFmt = '#,##0.000'; });

    // === Column Widths ===
    ws.columns = [
      { width: 6 }, { width: 14 }, { width: 18 }, { width: 40 },
      { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 },
    ];

    // === RTL ===
    if (isRTL) {
      ws.views = [{ rightToLeft: true, state: 'normal' }];
    }

    // === Download ===
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CustodyStatement_${c.custodyNumber}_${new Date().getTime()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(t.exportSuccess, { position: 'top-center' });
  };

  // === Styles ===
  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2 },
    '& .MuiInputLabel-root': { fontWeight: 500 },
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* === Title === */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <WalletIcon sx={{ fontSize: 36, color: '#1976d2' }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">{t.title}</Typography>
          <Typography variant="body2" color="textSecondary">{t.subtitle}</Typography>
        </Box>
      </Box>

      {/* === Filters === */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete
          options={custodies}
          getOptionLabel={(c: any) => `${isRTL ? (c.employeeNameAr || c.employeeName) : c.employeeName} (${c.custodyNumber})`}
          value={selectedCustody}
          onChange={(_, v) => { setSelectedCustody(v); setResult(null); }}
          sx={{ minWidth: 300, flex: 1 }}
          renderInput={(params) => <TextField {...params} label={t.selectCustody} size="small" sx={inputSx} />}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.fromDate}
            value={fromDate ? new Date(fromDate) : null}
            onChange={(date) => {
              const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setFromDate(newValue);
              if (toDate && newValue && toDate < newValue) setToDate('');
            }}
            format="dd/MM/yyyy"
            views={['year', 'month', 'day']}
            openTo="day"
            minDate={minDate}
            maxDate={toDate ? new Date(toDate) : maxDate}
            slotProps={{ textField: { size: 'small', sx: { width: 170, ...inputSx } } }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t.toDate}
            value={toDate ? new Date(toDate) : null}
            onChange={(date) => {
              const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
              setToDate(newValue);
              if (fromDate && newValue && fromDate > newValue) setFromDate('');
            }}
            format="dd/MM/yyyy"
            views={['year', 'month', 'day']}
            openTo="day"
            minDate={fromDate ? new Date(fromDate) : minDate}
            maxDate={maxDate}
            slotProps={{ textField: { size: 'small', sx: { width: 170, ...inputSx } } }}
          />
        </LocalizationProvider>
        <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading || !selectedCustody} sx={{ borderRadius: 2, fontWeight: 'bold', minWidth: 100 }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : t.search}
        </Button>
        <Tooltip title={t.reset}>
          <IconButton onClick={handleReset} color="default"><ReplayIcon /></IconButton>
        </Tooltip>
        {result && (
          <>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
              {t.print}
            </Button>
            <Button variant="outlined" color="success" startIcon={<ExcelIcon />} onClick={handleExportExcel} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
              {t.exportExcel}
            </Button>
          </>
        )}
      </Paper>

      {/* === No Data State === */}
      {!result && !loading && (
        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <WalletIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">{t.selectCustodyFirst}</Typography>
        </Paper>
      )}

      {/* === Results === */}
      {result && (
        <Box ref={printRef}>
          {/* --- Summary Cards --- */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Card elevation={3} sx={{ borderTop: '4px solid #2196f3', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <TrendingUpIcon sx={{ color: '#2196f3', fontSize: 28, mb: 0.5 }} />
                <Typography variant="caption" color="textSecondary" display="block">{t.totalReceived}</Typography>
                <Typography variant="h5" fontWeight="bold" color="#2196f3">{result.summary.totalReceived.toFixed(3)}</Typography>
                <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
              </CardContent>
            </Card>
            <Card elevation={3} sx={{ borderTop: '4px solid #f44336', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <TrendingDownIcon sx={{ color: '#f44336', fontSize: 28, mb: 0.5 }} />
                <Typography variant="caption" color="textSecondary" display="block">{t.totalSpent}</Typography>
                <Typography variant="h5" fontWeight="bold" color="#f44336">{result.summary.totalSpent.toFixed(3)}</Typography>
                <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
              </CardContent>
            </Card>
            <Card elevation={3} sx={{ borderTop: '4px solid #4caf50', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ReturnIcon sx={{ color: '#4caf50', fontSize: 28, mb: 0.5 }} />
                <Typography variant="caption" color="textSecondary" display="block">{t.totalReturned}</Typography>
                <Typography variant="h5" fontWeight="bold" color="#4caf50">{result.summary.totalReturned.toFixed(3)}</Typography>
                <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
              </CardContent>
            </Card>
            <Card elevation={3} sx={{ borderTop: `4px solid ${result.summary.closingBalance < 0 ? '#f44336' : '#1976d2'}`, borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <WalletIcon sx={{ color: result.summary.closingBalance < 0 ? '#f44336' : '#1976d2', fontSize: 28, mb: 0.5 }} />
                <Typography variant="caption" color="textSecondary" display="block">{t.closingBalance}</Typography>
                <Typography variant="h5" fontWeight="bold" color={result.summary.closingBalance < 0 ? '#f44336' : '#1976d2'}>
                  {result.summary.closingBalance.toFixed(3)}
                </Typography>
                <Typography variant="caption" color="textSecondary">{t.kwd}</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* --- Custody Info Bar --- */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', bgcolor: isDarkMode ? 'grey.900' : 'grey.50' }}>
            <Box>
              <Typography variant="caption" color="textSecondary">{t.custodyNumber}</Typography>
              <Typography variant="body1" fontWeight="bold">{result.custody.custodyNumber}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="textSecondary">{t.employee}</Typography>
              <Typography variant="body1" fontWeight="bold">
                {isRTL ? result.custody.employeeNameAr : result.custody.employeeName}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="textSecondary">{t.branch}</Typography>
              <Typography variant="body1">
                {isRTL ? (result.custody.branchNameAr || '-') : (result.custody.branchName || '-')}
              </Typography>
            </Box>
            {result.custody.accountCode && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="textSecondary">{t.account}</Typography>
                  <Typography variant="body1">
                    {result.custody.accountCode} - {isRTL ? result.custody.accountNameAr : result.custody.accountName}
                  </Typography>
                </Box>
              </>
            )}
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="textSecondary">{t.status}</Typography>
              <Chip
                label={(t as any)[result.custody.status] || result.custody.status}
                size="small"
                color={result.custody.status === 'Open' ? 'info' : result.custody.status === 'Settled' ? 'success' : 'default'}
                sx={{ fontWeight: 'bold', mt: 0.3 }}
              />
            </Box>
          </Paper>

          {/* --- Transactions Table --- */}
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 40 }}>{t.rowNum}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 100 }}>{t.date}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 140 }}>{t.type}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.description}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 110 }}>{t.received}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 110 }}>{t.spent}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 110 }}>{t.balance}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', width: 100 }}>{t.reference}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="textSecondary">{t.noData}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  result.transactions.map((tx: CustodyStatementLine, idx: number) => {
                    const config = typeConfig[tx.transactionType] || { color: '#666', icon: <ReceiptIcon fontSize="small" /> };
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          '&:nth-of-type(even)': { bgcolor: isDarkMode ? 'grey.900' : 'grey.50' },
                          '&:hover': { bgcolor: isDarkMode ? 'grey.800' : '#e3f2fd' },
                          transition: 'background 0.2s',
                        }}
                      >
                        <TableCell sx={{ textAlign: 'center', fontWeight: 500 }}>{tx.rowNumber}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>{tx.transactionDate.split('T')[0]}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Chip
                            icon={config.icon}
                            label={getTypeLabel(tx.transactionType)}
                            size="small"
                            sx={{
                              bgcolor: config.color + '18',
                              color: config.color,
                              fontWeight: 'bold',
                              fontSize: 11,
                              '& .MuiChip-icon': { color: config.color },
                            }}
                          />
                        </TableCell>
                        <TableCell>{isRTL ? tx.descriptionAr : tx.description}</TableCell>
                        <TableCell sx={{ textAlign: 'center', color: '#2e7d32', fontWeight: tx.received > 0 ? 'bold' : 'normal' }}>
                          {tx.received > 0 ? tx.received.toFixed(3) : '-'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', color: '#c62828', fontWeight: tx.spent > 0 ? 'bold' : 'normal' }}>
                          {tx.spent > 0 ? tx.spent.toFixed(3) : '-'}
                        </TableCell>
                        <TableCell sx={{
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: tx.balance < 0 ? '#c62828' : isDarkMode ? '#90caf9' : '#1565c0',
                        }}>
                          {tx.balance.toFixed(3)}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: 12 }}>{tx.referenceNumber || '-'}</TableCell>
                      </TableRow>
                    );
                  })
                )}
                {/* Totals Row */}
                {result.transactions.length > 0 && (
                  <TableRow sx={{ bgcolor: isDarkMode ? '#1a237e' : '#e3f2fd' }}>
                    <TableCell colSpan={4} sx={{ textAlign: isRTL ? 'left' : 'right', fontWeight: 'bold', fontSize: 13 }}>
                      {t.summary}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#2e7d32', fontSize: 13 }}>
                      {result.summary.totalReceived.toFixed(3)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', color: '#c62828', fontSize: 13 }}>
                      {(result.summary.totalSpent + result.summary.totalReturned).toFixed(3)}
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center', fontWeight: 'bold', fontSize: 13,
                      color: result.summary.closingBalance < 0 ? '#c62828' : '#1565c0',
                    }}>
                      {result.summary.closingBalance.toFixed(3)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default CustodyStatementPage;